import React, { useEffect, useState } from "react";
import api from "../lib/api";
import Card from "./Card";
import Switch from "./Switch";
import { Field } from "./ui-modal";
import { POST_PLATFORMS, socialContentsPerMonth } from "../lib/helpers";
import TagLists from "./TagLists";
import { toast } from "sonner";

// Les réglages du module Social Media d'une entreprise : le calendrier éditorial qu'elle utilise et
// le rythme convenu au contrat. Ils vivaient dans un onglet « Réseaux » de la fiche, au même rang que
// « Modules » — alors que ce sont les réglages d'UN module (décision du 25/10). Ils sont désormais
// dans la page de son module Social Media.
//
// Ce qui N'est PAS ici : les comptes sociaux de l'entreprise (`managed_networks`). Ils partageaient
// cet onglet mais ne décrivent pas une prestation, ils décrivent l'entreprise — ils sont remontés
// dans ses Coordonnées, à côté de son site.

export default function SocialMediaSettings({ clientId, client, onSaved }) {
  const [calCfg, setCalCfg] = useState({ calendar_mode: "native", calendar_external_name: "", calendar_external_url: "" });
  const [contrat, setContrat] = useState({ volumes: {}, same_content: false });

  useEffect(() => {
    if (!client) return;
    setCalCfg({ calendar_mode: client.calendar_mode || "native", calendar_external_name: client.calendar_external_name || "", calendar_external_url: client.calendar_external_url || "" });
    const sc = client.social_contract || {};
    setContrat({ volumes: sc.volumes || {}, same_content: !!sc.same_content });
  }, [client]);

  const reload = () => { onSaved && onSaved(); };

  const saveContrat = async () => {
    try { await api.put(`/admin/clients/${clientId}`, { social_contract: contrat }); toast.success("Contrat enregistré"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };

  const saveCal = async () => {
    if (calCfg.calendar_mode === "external" && !calCfg.calendar_external_url.trim()) return toast.error("Renseignez l'URL du lien externe");
    try { await api.put(`/admin/clients/${clientId}`, calCfg); toast.success("Calendrier éditorial mis à jour"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="module-social-settings">
      <TagLists clientId={clientId} />
      <Card title="Calendrier éditorial">
      <p className="text-sm text-marine/80 mb-4">Choisissez comment cette entreprise accède à son calendrier de publications.</p>
      <div className="flex gap-2 p-1 bg-sable-clair rounded-sm mb-4 max-w-md">
      <button type="button" onClick={() => setCalCfg({ ...calCfg, calendar_mode: "native" })} data-testid="calcfg-native"
      className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${calCfg.calendar_mode === "native" ? "bg-white text-marine shadow-sm" : "text-marine/80"}`}>Calendrier natif</button>
      <button type="button" onClick={() => setCalCfg({ ...calCfg, calendar_mode: "external" })} data-testid="calcfg-external"
      className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${calCfg.calendar_mode === "external" ? "bg-white text-marine shadow-sm" : "text-marine/80"}`}>Lien externe</button>
      </div>
      {calCfg.calendar_mode === "external" && (
      <div className="grid sm:grid-cols-2 gap-3 mb-4 max-w-xl">
      <Field label="Nom du lien"><input value={calCfg.calendar_external_name} onChange={(e) => setCalCfg({ ...calCfg, calendar_external_name: e.target.value })} data-testid="calcfg-name" className="inp" placeholder="Ex. Later" /></Field>
      <Field label="URL"><input value={calCfg.calendar_external_url} onChange={(e) => setCalCfg({ ...calCfg, calendar_external_url: e.target.value })} data-testid="calcfg-url" className="inp" placeholder="https://..." /></Field>
      </div>
      )}
      <button onClick={saveCal} data-testid="calcfg-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Enregistrer</button>
      </Card>

      <Card title="Contrat social media">
      <p className="text-sm text-marine/80 mb-4">Le rythme convenu au contrat. Il sert de point de départ à chaque mois ; le rythme d'un mois donné se règle sur la fiche de son projet, parce qu'un client peut demander d'alléger un mois et de rattraper le suivant.</p>
      <div className="grid sm:grid-cols-3 gap-3 mb-4 max-w-2xl" data-testid="contrat-volumes">
      {Object.keys(POST_PLATFORMS).map((r) => (
      <label key={r} className="text-sm">{POST_PLATFORMS[r].label}
      <input type="number" min="0" value={contrat.volumes[r] ?? ""} data-testid={`contrat-vol-${r}`}
      onChange={(e) => { const v = e.target.value; const next = { ...contrat.volumes }; if (v === "") delete next[r]; else next[r] = Math.max(0, parseInt(v) || 0); setContrat({ ...contrat, volumes: next }); }}
      className="inp mt-1" placeholder="—" />
      </label>
      ))}
      </div>
      <div className="flex items-center gap-3 mb-4">
      <Switch checked={contrat.same_content} onChange={() => setContrat({ ...contrat, same_content: !contrat.same_content })}
      testid="contrat-same-content" title="Les mêmes publications sur tous les réseaux" />
      <span className="text-sm">Les mêmes publications sur tous les réseaux</span>
      </div>
      <p className="text-sm text-bleu-fonce mb-4" data-testid="contrat-contenus">
      <b>{socialContentsPerMonth(contrat)}</b> contenu{socialContentsPerMonth(contrat) > 1 ? "s" : ""} à produire par mois
      </p>
      <button onClick={saveContrat} data-testid="contrat-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Enregistrer</button>
      </Card>
    </div>
  );
}
